import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { findProject, findCategory, projects } from '../data/researchData';
import './ResearchDetail.css';

const ResearchDetail = () => {
  const { categoryId, projectId } = useParams();
  const navigate = useNavigate();

  const project = findProject(categoryId, projectId);
  const category = findCategory(categoryId);
  const categoryProjects = projects[categoryId] || [];

  // Scroll to top on mount or when project changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId, projectId]);

  if (!project || !category) {
    return (
      <div className="rd-page">
        <div className="rd-container">
          <div className="rd-not-found">
            <h2>Project Not Found</h2>
            <p>The research project you're looking for doesn't exist.</p>
            <button className="rd-back-btn" onClick={() => navigate('/research')}>
              ← Back to Research
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Related projects (same category, exclude current)
  const relatedProjects = categoryProjects.filter(p => p.id !== projectId);

  // Extract DOI link from publication string
  const extractDOI = (pub) => {
    const doiMatch = pub.match(/DOI:\s*(10\.[^\s)]+)/i);
    if (doiMatch) return `https://doi.org/${doiMatch[1]}`;
    return null;
  };

  return (
    <div className="rd-page">
      <div className="rd-container">

        {/* Breadcrumb */}
        <nav className="rd-breadcrumb">
          <Link to="/" className="rd-crumb-link">Home</Link>
          <span className="rd-crumb-sep">»</span>
          <Link to="/research" className="rd-crumb-link">Research</Link>
          <span className="rd-crumb-sep">»</span>
          <span className="rd-crumb-current">{project.title}</span>
        </nav>

        {/* Page Title */}
        <h1 className="rd-title">{project.title}</h1>

        {/* Two-Column Layout */}
        <div className="rd-layout">

          {/* ===== LEFT COLUMN — Main Content ===== */}
          <div className="rd-content">

            {/* Overview */}
            <section className="rd-section">
              <p className="rd-overview-text">{project.fullDesc}</p>
            </section>

            {/* Research Objectives */}
            <section className="rd-section">
              <h2 className="rd-section-heading">Research Objectives</h2>
              <ul className="rd-objectives">
                {project.objectives.map((obj, i) => (
                  <li key={i} className="rd-obj-item">{obj}</li>
                ))}
              </ul>
            </section>

            {/* Key Technologies */}
            <section className="rd-section">
              <h2 className="rd-section-heading">Key Technologies</h2>
              <div className="rd-tech-tags">
                {project.technologies.map((tech, i) => (
                  <span key={i} className="rd-tech-tag">{tech}</span>
                ))}
              </div>
            </section>

            {/* Publications */}
            {project.publications && project.publications.length > 0 && (
              <section className="rd-section">
                <h2 className="rd-section-heading">Publications</h2>
                <ol className="rd-pub-list">
                  {project.publications.map((pub, i) => {
                    const doiLink = extractDOI(pub);
                    return (
                      <li key={i} className="rd-pub-item">
                        <span className="rd-pub-num">{i + 1}.</span>
                        <div className="rd-pub-body">
                          <span className="rd-pub-text">{pub}</span>
                          {doiLink && (
                            <a
                              href={doiLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rd-pub-link"
                            >
                              View Article →
                            </a>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {/* Contact CTA at bottom of content */}
            <section className="rd-section rd-contact-section">
              <button
                className="rd-contact-btn"
                onClick={() => window.location.href = "mailto:biman.mandal@iitg.ac.in"}
              >
                Contact for Collaboration
              </button>
            </section>
          </div>

          {/* ===== RIGHT COLUMN — Sidebar ===== */}
          <aside className="rd-sidebar">

            {/* Project Image */}
            <div className="rd-sidebar-img-wrap">
              <img
                src={project.image}
                alt={project.title}
                className="rd-sidebar-img"
              />
            </div>

            {/* Project Participants / Info Card */}
            <div className="rd-sidebar-card">
              <h3 className="rd-sidebar-title">Project Details</h3>

              <div className="rd-sidebar-rows">
                <div className="rd-sidebar-row">
                  <span className="rd-sidebar-label">Category</span>
                  <span className="rd-sidebar-value">{category.name}</span>
                </div>
                <div className="rd-sidebar-row">
                  <span className="rd-sidebar-label">Publications</span>
                  <span className="rd-sidebar-value">{project.publications?.length || 0}</span>
                </div>
                <div className="rd-sidebar-row">
                  <span className="rd-sidebar-label">Technologies</span>
                  <span className="rd-sidebar-value">{project.technologies?.length || 0}</span>
                </div>
              </div>
            </div>

            {/* Related Projects */}
            {relatedProjects.length > 0 && (
              <div className="rd-sidebar-card">
                <h3 className="rd-sidebar-title">Related Projects</h3>
                <div className="rd-related-list">
                  {relatedProjects.slice(0, 5).map((rp) => (
                    <Link
                      key={rp.id}
                      to={`/research/${categoryId}/${rp.id}`}
                      className="rd-related-item"
                    >
                      <img src={rp.image} alt={rp.title} className="rd-related-thumb" />
                      <div className="rd-related-info">
                        <span className="rd-related-name">{rp.title}</span>
                        <span className="rd-related-desc">{rp.description}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Bottom Back Button */}
        <div className="rd-bottom-nav">
          <button className="rd-back-btn" onClick={() => navigate('/research')}>
            ← Back to All Research Areas
          </button>
        </div>

      </div>
    </div>
  );
};

export default ResearchDetail;